import i18n from 'simple-react-i18n'
import Input from '../../../../components/forms/Input'
import { getDate, getDateWithHour, getHour } from '../../../../utils/DateUtil'
import { onChangeDate, onChangeHour } from '../../../../utils/FormUtils'
import React, { useState } from 'react'
import { Card, CardContent, Grid } from '@mui/material'
import { CardTitle } from '../../../../components/card/NewCard'
import Button from '../../../../components/forms/Button'
import PluviometryAction from '../../../actions/PluviometryAction'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import DtoPluvioMeasures from '../../../dto/measures/DtoPluvioMeasures'
import ConfirmModal from '../../../../components/modal/ConfirmModal'
import NumberField from '../../../../components/forms/NumberField'
import ToastrAction from '../../../../toastr/actions/ToastrAction'
import { isNull, round } from 'lodash'
import useUpdateEffect from '../../../../utils/customHook/useUpdateEffect'
import { measurePluvioType } from '../../../shapes/PluvioShapes'

const CompensationPluvioToolPanel = ({
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    isChange,
    setIsChange,
    createTechnicalEvent,
    measuresSelectedByFilters,
    setSerieLinePrevious,
    setIsPreview,
    measuresData,
    serieLinePrevious,
}) => {
    const [modalOpen, setModalOpen] = useState(false)
    const [isPreviewButton, setIsPreviewButton] = useState(false)

    const [coeff, setCoeff] = useState(1)
    const [offset, setOffset] = useState(0)

    const dispatch = useDispatch()

    useUpdateEffect(() => {
        if (!isPreviewButton && serieLinePrevious.length && measuresSelectedByFilters.length) {
            setOffset(round(serieLinePrevious[0].value - measuresSelectedByFilters[0].value, 2))
        }
    }, [serieLinePrevious])

    const onValidate = () => {
        if (isNull(startDate) || isNull(endDate)) {
            dispatch(ToastrAction.warning(i18n.datesNoSelected))
            return
        }
        setIsPreviewButton(true)
        setIsPreview(true)

        const dataPluvio = measuresData[0].measures.map(d => ([ d.date, d.value ]))
        const measuresPrevious = measuresSelectedByFilters.map(m => ({
            date: m.date,
            value: (m.value * coeff) + offset,
        }),
        )

        const findIndexStartDate = dataPluvio.findIndex(m => m[0] === startDate) - 1
        const measureBeforeStartDate = dataPluvio[findIndexStartDate]

        const findIndexEndDate = dataPluvio.findIndex(m => m[0] === endDate) + 1
        const measureAfterStartDate = dataPluvio[findIndexEndDate]

        const allMeasuresPreview = [
            ...(measureBeforeStartDate ? [{
                date: measureBeforeStartDate[0],
                value: measureBeforeStartDate[1],
            }] : []),
            ...measuresPrevious,
            ...(measureAfterStartDate ? [{
                date: measureAfterStartDate[0],
                value: measureAfterStartDate[1],
            }] : []),
        ]

        setSerieLinePrevious(allMeasuresPreview)
    }


    const onCancel = () => {
        setIsPreviewButton(false)
        setIsPreview(false)
        setSerieLinePrevious(measuresSelectedByFilters)
    }

    const onSave = () => {
        setIsPreviewButton(false)
        setIsPreview(false)
        setModalOpen(true)
        const measuresChanges = measuresSelectedByFilters.map(m => ({
            ...m,
            value: (m.value * coeff) + offset,
            updated: true,
        }),
        )
        setOffset(0)
        setCoeff(1)
        return (
            <ConfirmModal
                isOpen={modalOpen}
                title={i18n.compensation}
                onValidate={
                    dispatch(PluviometryAction.updateMeasures(measuresChanges))
                        .then(() => setIsChange(!isChange))
                        .then(() => createTechnicalEvent({ mode: 'compensation' }))
                }
                onClose={() => setModalOpen(false)}
            />
        )
    }

    const resetDates = () => {
        setStartDate(null)
        setEndDate(null)
    }


    return (
        <div>
            <Card>
                <CardTitle title={i18n.compensation} />
                <CardContent>
                    <div className='padding-top-1'/>
                    <Grid container sx={{ paddingBottom: 1 }}>
                        <Grid size={12} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Button
                                tooltip={ i18n.resetDates }
                                icon='loop'
                                className='margin-left-1 validToolBtn'
                                onClick={ () => resetDates() }
                                disabled={ isPreviewButton }
                            />
                            <h6 className='padding-left-1'>
                                { i18n.resetDates }
                            </h6>
                        </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                        <Grid size={6}>
                            <Input
                                title={ i18n.startDate }
                                id='startDate'
                                value={ getDate(startDate)}
                                onChange={ v => {
                                    onChangeDate(v, v2 => {
                                        return setStartDate(getDateWithHour(v2, startDate).valueOf())
                                    })
                                } }
                                disabled={ isPreviewButton }
                            />
                        </Grid>
                        <Grid size={6}>
                            <Input
                                title={ i18n.startHour }
                                value={ getHour(startDate) }
                                onChange={ v => {
                                    onChangeHour(v, v2 => {
                                        return setStartDate(getDateWithHour(startDate, v2).valueOf())
                                    })
                                } }
                                disabled={ isPreviewButton }
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                        <Grid size={6}>
                            <Input
                                id='endDate'
                                value={ getDate(endDate) }
                                title={ i18n.endDate }
                                onChange={ v => {
                                    onChangeDate(v, v2 => {
                                        return setEndDate(getDateWithHour(v2, endDate).valueOf())
                                    })
                                } }
                                disabled={ isPreviewButton }
                            />
                        </Grid>
                        <Grid size={6}>
                            <Input
                                id='endDateHour'
                                title={ i18n.endHour }
                                value={ getHour(endDate) }
                                onChange={ v => {
                                    onChangeHour(v, v2 => {
                                        return setEndDate(getDateWithHour(endDate, v2).valueOf())
                                    })
                                } }
                                disabled={ isPreviewButton }
                            />
                        </Grid>
                    </Grid>
                    <Grid container sx={{ paddingLeft: 1 }}>
                        <Grid size={12}>
                            <h6>{ i18n.compensationFormula }</h6>
                        </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                        <Grid size={6}>
                            <NumberField
                                title={ i18n.coeff }
                                value={ coeff }
                                onChange={ v => setCoeff(v) }
                                disabled={ isPreviewButton }
                                floatValue
                            />
                        </Grid>
                        <Grid size={6}>
                            <NumberField
                                title={ i18n.offset }
                                value={ offset }
                                onChange={ v => setOffset(v) }
                                disabled={ isPreviewButton }
                                floatValue
                            />
                        </Grid>
                    </Grid>
                    <Grid container sx={{ paddingBottom: 1, paddingTop: 1, textAlign: 'center' }}>
                        <Grid size={12}>
                            <Button
                                tooltip={ i18n.apply }
                                onClick={ onValidate }
                                icon='border_color'
                                className='btn-floating btn-large'
                                disabled={ isPreviewButton }
                            />
                            <Button
                                tooltip={ i18n.cancel }
                                onClick={ onCancel }
                                icon='cancel'
                                className='red btn-floating btn-large margin-left-2 margin-right-2'
                                disabled={ !isPreviewButton }
                            />
                            <Button
                                tooltip={ i18n.register }
                                onClick={ onSave }
                                icon='save'
                                disabled={ !isPreviewButton }
                                className={ `btn-floating btn-large ${isPreviewButton ? 'pulse' : ''}` }
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </div>

    )
}


CompensationPluvioToolPanel.propTypes = {
    startDate: PropTypes.number,
    setStartDate: PropTypes.func,
    endDate: PropTypes.number,
    setEndDate: PropTypes.func,
    isChange: PropTypes.bool,
    setIsChange: PropTypes.func,
    isPreview: PropTypes.bool,
    setIsPreview: PropTypes.func,
    createTechnicalEvent: PropTypes.func,
    measuresSelectedByFilters: PropTypes.arrayOf(measurePluvioType),
    setSerieLinePrevious: PropTypes.func,
    measuresData: PropTypes.arrayOf(PropTypes.instanceOf(DtoPluvioMeasures)),
    serieLinePrevious: PropTypes.arrayOf(measurePluvioType),
}

export default CompensationPluvioToolPanel
